import React, { FC, Fragment, useCallback, useContext, useRef } from 'react'

import { pick } from 'lodash-es'
import { isMobile } from 'react-device-detect'
import Sticky from 'react-stickynode'

import { AppContext } from 'Const'
import { CartItemTypes } from 'Types'
import { scrollToTopOfElement } from 'Utils'
import { isDelivery, isShipping } from 'Utils/cartFulfillment'

import ApplyDiscount from './ApplyDiscount'
import CartItem from './CartItem'
import Discounts from './Discounts'
import DriverTips from './DriverTips'
import TaxAndFees from './TaxAndFees'
import Tips from './Tips'
import Utensils from './Utensils'

const OrderDetails: FC = () => {
  const { shop, cart } = useContext(AppContext)
  const containerRef = useRef<HTMLDivElement>(null)
  const { fulfillment } = cart
  const driverTipVariants = isDelivery(fulfillment) ? fulfillment.scheduleSetting.driverTipVariants : []

  const scrollToTop = useCallback(() => {
    if (isMobile) scrollToTopOfElement(containerRef, 1)
  }, [])

  return (
    <div ref={containerRef} className="order-1 order-lg-2 / col-lg-5 / mb-6 mb-lg-0">
      <div className="checkout-order">
        <div className="checkout-order__products">
          <h2 className="checkout-order__products-title / h6">Order Summary</h2>
          <div className="checkout-order__products-scroll">
            {cart.cartItems.map((cartItem) => (
              <CartItem key={cartItem.id} {...cartItem} />
            ))}
            {shop.checkoutSetting?.enableUtensils &&
            cart.cartItems.some((cartItem) => cartItem.type === CartItemTypes.MENU_ITEMABLE) ? (
              <Utensils utensils={cart.utensils} />
            ) : null}
          </div>
        </div>

        {shop.tipVariants.length ? <Tips /> : null}
        {driverTipVariants.length ? <DriverTips driverTipVariants={driverTipVariants} /> : null}

        {/* Order subtotal amount */}
        <div className="checkout-order__info">
          <span>Subtotal</span>
          <span>{cart.totalPriceFormatted}</span>
        </div>

        {/* Order details */}
        <ul className="checkout-order__details">
          {isShipping(cart.fulfillment) ? (
            <li>
              <span>Shipping cost</span>
              <span>{cart.freeShipping ? 'FREE' : cart.totalShippingCostMoneyFormatted}</span>
            </li>
          ) : null}

          {isDelivery(cart.fulfillment) ? (
            <li>
              <span>Delivery</span>
              <span>{cart.totalDeliveryCostMoneyFormatted}</span>
            </li>
          ) : null}

          <Discounts
            appliedDiscountCodes={cart.appliedDiscountCodes}
            hidden={!cart.totalDiscountMoney}
            totalDiscountMoneyFormatted={cart.totalDiscountMoneyFormatted}
          />

          <li hidden={!cart.appliedTipVariant?.amount}>
            <span>{driverTipVariants?.length || cart.itemsTotalTipMoney ? 'Restaurant tip' : 'Tip'}</span>
            <span>{cart.appliedTipVariant?.amountFormatted}</span>
          </li>

          <li hidden={!cart.driverTipMoney}>
            <span>Driver Tip</span>
            <span>{cart.driverTipMoneyFormatted}</span>
          </li>

          <li hidden={!cart.itemsTotalTipMoney}>
            <span>Tip</span>
            <span>{cart.itemsTotalTipMoneyFormatted}</span>
          </li>

          <TaxAndFees
            {...pick(cart, [
              'absorbShopAppFee',
              'customFees',
              'hstForAppFeeMoney',
              'hstForAppFeeMoneyFormatted',
              'hstForDeliveryCostMoney',
              'hstForDeliveryCostMoneyFormatted',
              'shopAppFeeMoney',
              'shopAppFeeMoneyFormatted',
              'totalTaxAndFeesMoneyFormatted',
              'totalTaxMoneyFormatted',
            ])}
          />

          <li hidden={!cart.totalGiftCardMoney}>
            {cart.appliedGiftCards.length > 1 ? (
              <span>
                Gift cards:
                <br />
                {cart.appliedGiftCards.map(({ cardLast4 }) => (
                  <Fragment key={cardLast4}>
                    {cardLast4}
                    <br />
                  </Fragment>
                ))}
              </span>
            ) : (
              <span>Gift card - {cart.appliedGiftCards[0]?.cardLast4}</span>
            )}

            <span>-{cart.totalGiftCardMoneyFormatted}</span>
          </li>
        </ul>

        {/* Order total amount */}
        <Sticky innerClass="w-100 checkout-order__info_sticky" top=".js-navbar">
          <div className="checkout-order__info / font-weight-bold" onClick={scrollToTop}>
            <span>Order total</span>
            <span>{cart.totalMoneyFormatted}</span>
          </div>
        </Sticky>

        {/* Apply discount code */}
        {shop.hasDiscountCodes ? (
          <div className="checkout-order__info">
            <ApplyDiscount />
          </div>
        ) : null}

        {/* Cart note */}
        {cart.note ? (
          <div className="checkout-order__info / flex-wrap">
            <span className="d-block / mb-2 / w-100">Order note:</span>
            <p>{cart.note}</p>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default OrderDetails
